import {
    DefaultTheme,
    IBody,
    IJson,
    IMutableWidgetsMap,
    TFetchJson,
    TFetchJsonContext,
    TGetBody,
    TInitPayerOptions,
    TRequestOptions,
    TWAction,
} from '@libs/types';
import { EntityId } from '@reduxjs/toolkit';
import {
    clearEmbeddedPlayerLocale,
    clearEmbeddedPlayerSession,
    getEmbeddedPlayerLocale,
    getEmbeddedPlayerSession,
    setEmbeddedPlayerLocale,
    setEmbeddedPlayerSession,
} from '../embeddedPlayerService';
import { AccessDeniedError } from '../errors/AccessDeniedError';
import { CFAccessDeniedError } from '../errors/CFAccessDeniedError';
import { toRfc3339WithOffset } from '../request/dateHelper';
import { getDeviceType } from '../request/deviceHelper';
import {
    AUTH,
    fetchWithDelay,
    getActionData,
    getOsDetails,
    getScreenPrint,
    hasScenario,
    hasScenarioWidgets,
    isCanDoRetry,
    isSupportMultiTabs,
} from '../request/requestHelper';
import { getDeviceId, getLocale, isValidLocale } from '../utils';
import { INetworkProvider, TProviderSession } from './networkProvider.types';

declare const VERSION: string;

/** Stored auth is a JSON object; anything else (e.g. a bare deviceId string) is ignored. */
const parseStoredAuth = (raw: string) => {
    try {
        return JSON.parse(raw) as Record<string, string>;
    } catch (e) {
        // Suppress: value is not a valid JSON auth object
        console.error(e);
        return undefined;
    }
};

/**
 * Locale for `session.environment.locale`. A valid `initPayerOptions.locale` of the requestInteraction wins and,
 * for an embedded player, is kept by `playerId` so every later request of that player sends it too.
 * A requestInteraction started by `init` (it carries `initPayerOptions`) without a valid locale drops the kept one,
 * so a re-init follows the page-wide locale like the client side does. Otherwise falls back to {@link getLocale}.
 */
const resolveSessionLocale = (isRequestInteraction: boolean, playerId?: string, initPayerOptions?: TInitPayerOptions) => {
    const isInitRequest = isRequestInteraction && !!initPayerOptions;
    const initLocale = isInitRequest ? initPayerOptions?.locale : undefined;
    if (isValidLocale(initLocale)) {
        if (playerId) {
            setEmbeddedPlayerLocale(playerId, initLocale);
        }
        return initLocale;
    }
    if (initLocale !== undefined) {
        console.warn(`Ignored an invalid init locale: ${String(initLocale)}`);
    }
    if (playerId && isInitRequest) {
        clearEmbeddedPlayerLocale(playerId);
    }
    return (playerId && getEmbeddedPlayerLocale(playerId)) || getLocale();
};

/**
 * Builds the API service around a platform provider. All providers share the same body assembly,
 * retry logic and auth persistence — only `INetworkProvider` differs between them.
 */
export const createApiService = <TExtraData extends object = object>(provider: INetworkProvider<TExtraData>) => {
    provider.init?.();

    const getSessionObject = async (
        isRequestInteraction: boolean,
        playerId?: string,
        initPayerOptions?: TInitPayerOptions,
    ): Promise<TProviderSession<TExtraData>> => {
        const deviceId = getDeviceId();
        const osDetails = await getOsDetails();
        const isMatchesDark = window.matchMedia('(prefers-color-scheme: dark)')?.matches;
        const platform = provider.platform ?? 'Web';
        const currentDate = new Date();
        const session: TProviderSession<TExtraData> = {
            environment: {
                realm: platform,
                locale: resolveSessionLocale(isRequestInteraction, playerId, initPayerOptions),
                data: {
                    userAgent: navigator?.userAgent,
                    deviceTheme: isMatchesDark ? DefaultTheme.dark : DefaultTheme.light,
                    operatingSystem: `${osDetails.name}${osDetails.version ? ' ' + osDetails.version : ''}`,
                    deviceType: getDeviceType(osDetails.formFactors),
                    id: `${platform.toUpperCase()}-${deviceId}`,
                    deviceId: deviceId,
                    authApp: `${platform} ${VERSION}`,
                    authSource: platform,
                    screenHeight: window?.innerHeight?.toString(),
                    screenWidth: window?.innerWidth?.toString(),
                    browserTimeZone: Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone,
                    browserTimeZoneOffset: currentDate.getTimezoneOffset?.().toString?.(),
                    requestTimestamp: toRfc3339WithOffset(currentDate),
                    colorDepth: `${window?.screen?.colorDepth}`,
                    screenPrint: getScreenPrint(),
                    ...(provider.getExtraEnvData?.() ?? ({} as TExtraData)),
                },
            },
        };
        const requestUri = provider.getRequestUri?.(isRequestInteraction, initPayerOptions);
        if (requestUri) {
            session.environment.requestUri = requestUri;
            // TODO: think how implement this pasrt for telegtram
        } else if (isRequestInteraction && initPayerOptions?.requestUri) {
            session.environment.requestUri = initPayerOptions?.requestUri;
        }
        if (osDetails?.architecture) {
            session.environment.data.model = osDetails?.architecture;
        }
        if (playerId) {
            const embeddedSession = getEmbeddedPlayerSession(playerId, isRequestInteraction);
            if (embeddedSession?.auth) {
                session.auth = embeddedSession.auth;
            }
            if (embeddedSession?.isFillParent) {
                session.parent = { ...session };
            }
        } else {
            const authSessionStorage = sessionStorage?.getItem(AUTH);
            if (authSessionStorage) {
                session.auth = parseStoredAuth(authSessionStorage);
            } else {
                const authLocalStorage = localStorage?.getItem(AUTH);
                // If the value is valid JSON it's an auth object; if not, it's a deviceId string and parsing fails.
                const parsedAuth = authLocalStorage ? parseStoredAuth(authLocalStorage) : undefined;
                if (parsedAuth) {
                    if (isSupportMultiTabs()) {
                        session.parent = { ...session, auth: parsedAuth };
                    } else {
                        session.auth = parsedAuth;
                    }
                }
            }
        }
        if (isRequestInteraction && initPayerOptions?.externalData && Object.keys(initPayerOptions.externalData).length > 0) {
            session.environment.data.externalData = initPayerOptions.externalData;
        }
        const extensions = await provider.getExtensions?.(isRequestInteraction, initPayerOptions);
        if (extensions && Object.keys(extensions).length > 0) {
            session.environment.data.extensions = extensions;
        }
        return session;
    };

    const getBody: TGetBody = async <TData, TOptions>(
        action?: TWAction | undefined,
        currentPageId?: string,
        mutableWidgetsMap?: IMutableWidgetsMap<TData, TOptions>,
        playerId?: string,
        initPayerOptions?: TInitPayerOptions,
    ) => {
        if (action?.clearSession) {
            if (playerId) {
                clearEmbeddedPlayerSession(playerId);
            } else {
                sessionStorage?.removeItem(AUTH);
                localStorage?.removeItem(AUTH);
            }
        }
        const body: IBody = { session: await getSessionObject(!action, playerId, initPayerOptions) };
        if (action) {
            const pageId: EntityId = currentPageId ?? '';
            const data: ReturnType<typeof getActionData> = getActionData(Object.values(mutableWidgetsMap ?? {}));
            body.action = { ...action, data, page: pageId };
        }
        return body;
    };

    const fetchJsonAttempt = async <TData, TOptions>(
        action: TWAction | undefined,
        context: TFetchJsonContext<TData, TOptions>,
        options: TRequestOptions,
        attempt: number,
    ): Promise<IJson> => {
        const { currentPageId, mutableWidgetsMap, setPlayerState } = context;
        const { playerId, initPayerOptions } = options;
        if (provider.canFetch && !provider.canFetch()) {
            throw provider.notAllowedError?.() ?? new Error('fetchJson: the network provider does not allow this request.');
        }
        try {
            const body: IBody = await getBody(action, currentPageId, mutableWidgetsMap, playerId, initPayerOptions);

            const json = await fetchWithDelay(body, action, setPlayerState, options);
            if (isCanDoRetry(action, attempt)) {
                if (!hasScenario(json)) {
                    sessionStorage?.removeItem(AUTH);
                    localStorage?.removeItem(AUTH);
                }
                if (!hasScenarioWidgets(json)) {
                    return fetchJsonAttempt(action, context, options, ++attempt);
                }
            }
            if (hasScenarioWidgets(json)) {
                if (json?.session?.auth) {
                    if (playerId) {
                        setEmbeddedPlayerSession(playerId, { auth: json.session.auth });
                    } else {
                        if (isSupportMultiTabs()) {
                            sessionStorage.setItem(AUTH, JSON.stringify(json.session.auth));
                        }
                        localStorage.setItem(AUTH, JSON.stringify(json.session.auth));
                    }
                }
                return json;
            }
        } catch (e) {
            const idAccessDeniedError = e instanceof AccessDeniedError;
            const isCFAccessDeniedError = e instanceof CFAccessDeniedError;
            if (isCFAccessDeniedError) {
                console.error('fetchJson: CFAccessDeniedError', e);
                throw e;
            } else if (!idAccessDeniedError && isCanDoRetry(action, attempt)) {
                return fetchJsonAttempt(action, context, options, ++attempt);
            } else {
                console.error('fetchJson', e);
                throw e;
            }
        }
        throw new Error('fetchJson: has no scenario widgets.');
    };

    const fetchJson: TFetchJson = (action, context, options = {}) => fetchJsonAttempt(action, context, options, 1);

    return { getSessionObject, getBody, fetchJson };
};
